import React, { useEffect, useState } from 'react';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Account, Budget } from '../../interfaces/accounting.interface';
import { BudgetService } from '../../services/budget.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { AuthService } from '../../services/auth.service';
import { postingAccounts } from '../../utils/accountHierarchy';
import { useToast } from '../../context/ToastContext';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

interface BudgetFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Period the form opens on (defaults to the current month). */
    initialYear?: number;
    initialMonth?: number;
}

const BudgetForm: React.FC<BudgetFormProps> = ({ open, onClose, onSuccess, initialYear, initialMonth }) => {
    const toast = useToast();
    const currentDate = new Date();
    const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);

    const [accountId, setAccountId] = useState('');
    const [year, setYear] = useState<number>(initialYear ?? currentDate.getFullYear());
    const [month, setMonth] = useState<number>(initialMonth ?? currentDate.getMonth() + 1);
    const [budgetAmount, setBudgetAmount] = useState<number>(0);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            fetchExpenseAccounts();
            resetForm();
        }
    }, [open]);

    const fetchExpenseAccounts = async () => {
        try {
            const response = await AccountService.getAccountsByType('EXPENSE');
            const data = Array.isArray(response) ? response : (response.content || []);
            setExpenseAccounts(postingAccounts(data));
        } catch (err) {
            console.error('Error fetching expense accounts:', err);
        }
    };

    const resetForm = () => {
        setAccountId('');
        setYear(initialYear ?? currentDate.getFullYear());
        setMonth(initialMonth ?? currentDate.getMonth() + 1);
        setBudgetAmount(0);
        setNotes('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!accountId) {
            toast.showError('Please select an account');
            return;
        }
        if (budgetAmount <= 0) {
            toast.showError('Budget amount must be greater than zero');
            return;
        }

        try {
            const institutionId = AuthService.getSelectedInstitution();
            const budgetData: Budget = {
                accountId,
                year,
                month,
                budgetAmount: Number(budgetAmount),
                notes: notes || undefined,
                institutionId
            };
            await BudgetService.createBudget(budgetData);
            toast.showSuccess('Budget saved');
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error creating budget:', err);
            toast.showApiError(err, 'Failed to create budget');
        }
    };

    return (
        // A Dialog (not a hand-rolled overlay) so the account dropdown opens above it.
        <Dialog data-help-page="accounting.budgetForm.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Set Budget</DialogTitle>
                <DialogContent>
                    <div style={{ marginTop: 8 }}>
                        <AccountAutocomplete data-help="accounting.budgetForm.field.account"
                            accounts={expenseAccounts}
                            value={accountId}
                            onChange={(id) => setAccountId(id)}
                            label="Account (Expense)"
                            required
                        />
                    </div>
                    <TextField data-help="accounting.budgetForm.field.year"
                        fullWidth
                        label="Year"
                        type="number"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        required
                        margin="normal"
                        inputProps={{ min: 2000, max: 2100 }}
                    />
                    <FormControl data-help="accounting.budgetForm.field.month" fullWidth margin="normal" required>
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={month}
                            label="Month"
                            onChange={(e) => setMonth(Number(e.target.value))}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField data-help="accounting.budgetForm.field.amount"
                        fullWidth
                        label="Budget Amount"
                        type="number"
                        value={budgetAmount}
                        onChange={(e) => setBudgetAmount(Number(e.target.value))}
                        required
                        margin="normal"
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField data-help="accounting.budgetForm.field.notes"
                        fullWidth
                        label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        margin="normal"
                        multiline
                        rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.budgetForm.cancel" onClick={onClose}>Cancel</Button>
                    <Button data-help="accounting.budgetForm.save" type="submit" variant="contained" color="primary">
                        Save Budget
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BudgetForm;
